"use client";

import styled, { css } from "styled-components";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { LucideIcon } from "lucide-react";

interface SummaryCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  variant?: "default" | "success" | "danger";
  subtitle?: string;
  isLoading?: boolean;
  hasEstimate?: boolean;
}

const CardContentWrapper = styled.div`
  padding: 24px;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin: 0;
`;

const valueColorMap = {
  default: "var(--color-text-primary)",
  success: "var(--color-success)",
  danger: "var(--color-error)",
};

const Value = styled.p<{ $variant: "default" | "success" | "danger"; $hasEstimate?: boolean }>`
  font-size: 24px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: ${({ $variant }) => valueColorMap[$variant]};
  ${({ $hasEstimate }) => $hasEstimate && css`cursor: help;`}
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: var(--color-text-tertiary);
  margin: 4px 0 0 0;
`;

const iconBgMap = {
  default: "var(--color-accent-light)",
  success: "var(--color-success-border)",
  danger: "var(--color-error-border)",
};

const iconColorMap = {
  default: "var(--color-accent)",
  success: "var(--color-success)",
  danger: "var(--color-error)",
};

const IconWrapper = styled.div<{ $variant: "default" | "success" | "danger" }>`
  padding: 12px;
  border-radius: 50%;
  background-color: ${({ $variant }) => iconBgMap[$variant]};
  color: ${({ $variant }) => iconColorMap[$variant]};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export function SummaryCard({
  title,
  value,
  icon: Icon,
  variant = "default",
  subtitle,
  isLoading,
  hasEstimate,
}: SummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContentWrapper>
          <FlexContainer>
            <LoadingSection>
              <Skeleton width={96} height={16} />
              <Skeleton width={128} height={32} />
              <Skeleton width={80} height={12} />
            </LoadingSection>
            <Skeleton width={48} height={48} borderRadius="50%" />
          </FlexContainer>
        </CardContentWrapper>
      </Card>
    );
  }

  const displayValue = hasEstimate ? `~${value}` : value;

  return (
    <Card>
      <CardContentWrapper>
        <FlexContainer>
          <InfoSection>
            <Title>{title}</Title>
            {hasEstimate ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Value $variant={variant} $hasEstimate>
                      {displayValue}
                    </Value>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Содержит предварительные данные</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Value $variant={variant}>{displayValue}</Value>
            )}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </InfoSection>
          <IconWrapper $variant={variant}>
            <Icon />
          </IconWrapper>
        </FlexContainer>
      </CardContentWrapper>
    </Card>
  );
}
